import {
  SubPageBlueprint,
  createFrontendModule,
} from '@backstage/frontend-plugin-api';
import { Content } from '@backstage/core-components';
import { PageShareTitle } from '../../lib/shareTitle';
import { getStoredFunMode, translate } from '../i18n';

/**
 * Overrides the user-settings plugin's General sub-page (extension id
 * `sub-page:user-settings/general` — same blueprint, name and path as
 * upstream, so this module replaces it) with a composition that omits the
 * "Pin Sidebar" toggle. The toggle is hardcoded inside the stock
 * UserSettingsAppearanceCard (not an extension of its own, so it cannot be
 * disabled via app-config); replacing the sub-page is the clean removal.
 */
const settingsGeneralPage = SubPageBlueprint.make({
  name: 'general',
  params: {
    path: 'general',
    // Non-component config (evaluated once at extension registration), so
    // this reads the fun-mode flag directly instead of the useT() hook — see
    // the i18n module's non-component-code note.
    title: translate('settings.general.page.title', getStoredFunMode()),
    loader: () =>
      import('./SettingsGeneralPage').then(m => (
        <PageShareTitle title="Settings">
          <Content>
            <m.SettingsGeneralPage />
          </Content>
        </PageShareTitle>
      )),
  },
});

/**
 * Overrides the stock Authentication Providers sub-page
 * (`sub-page:user-settings/auth-providers`) with a design-system list of the
 * configured providers (the upstream one renders an MUI InfoCard + List). Drops
 * the upstream `providerSettings` extension input, which this app doesn't use.
 */
const settingsAuthProvidersPage = SubPageBlueprint.make({
  name: 'auth-providers',
  params: {
    path: 'auth-providers',
    // Non-component config (evaluated once at extension registration) — see
    // the note on the General sub-page above.
    title: translate('settings.authProviders.page.title', getStoredFunMode()),
    loader: () =>
      import('./SettingsAuthProvidersPage').then(m => (
        <PageShareTitle title="Authentication providers">
          <Content>
            <m.SettingsAuthProvidersPage />
          </Content>
        </PageShareTitle>
      )),
  },
});

export const settingsModule = createFrontendModule({
  pluginId: 'user-settings',
  extensions: [settingsGeneralPage, settingsAuthProvidersPage],
});
